import { Autocomplete } from '@mui/material';
import { memo, useCallback, useMemo } from 'react';
import { __MANAGEMENT_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponseList } from 'shell/models/http-response';
import { AxiosResponse } from 'axios';
import PmiTextField from 'shell/components/shared/text-field';
import debounce from 'shell/utils/debounce';
import { noop } from 'shell/utils/noop';
import { Operation } from 'shell/view-models/operation';

interface MunicipalityListProps {
  disabled?: boolean;
  value?: StationIdAnprListVM | null;
  onChange: (value: StationIdAnprListVM | null) => void;
  error?: boolean;
  required?: boolean;
  label?: string;
  id?: string;
}

interface StationIdAnpr {
  key: {
    codice?: number;
  };
  codicePostazioneAnpr?: string;
}

export interface StationIdAnprListVM {
  id?: number;
  description: string;
}

const StationIdAnprListComponent = ({
  disabled,
  onChange,
  required,
  value: selectedValue,
  error,
  label = 'Comuni',
  id = 'municipality',
}: MunicipalityListProps): React.ReactElement => {
  const [list, getList] = useHttpFetch<HttpResponseList<StationIdAnpr>, Array<StationIdAnprListVM>>(
    `${__MANAGEMENT_API_URL__}/postazione-anpr`,
    {
      onBootstrap: false,
      initialData: [],
      options: {
        loading: false,
        errorCatching: true,
      },
      transformer: (response: AxiosResponse<HttpResponseList<StationIdAnpr>>) => {
        const { data } = response;

        return data?.payload.list.map((item) => ({
          id: item.key?.codice || undefined,
          description: item.codicePostazioneAnpr || '',
        })) as Array<StationIdAnprListVM>;
      },
    }
  );

  const defaultProps = useMemo(
    () => ({
      options: list.data as Array<StationIdAnprListVM>,
      getOptionLabel: (option: StationIdAnprListVM) => {
        return option.description;
      },
    }),
    [list]
  );

  const handleSearch = debounce<string>(
    useCallback(
      (value) => {
        const { abort = noop } = list;

        abort();

        getList({
          params: {},
          paramsSerializer: () =>
            `filters=codicePostazioneAnpr:${value?.trim()}:${
              Operation.LIKE
            }&filters=unused:true&filters=attivi:true`,
        });
      },
      [list.abort, getList]
    )
  );

  return (
    <Autocomplete
      {...defaultProps}
      id={id}
      noOptionsText="Nessun elemento"
      loadingText="Caricamento..."
      disabled={disabled}
      value={selectedValue}
      onChange={(event, element) => {
        event.stopPropagation();
        onChange(element);
      }}
      onInputChange={(event, value, reason) => {
        if (value.length >= 3 && reason === 'input') {
          handleSearch(value);
        }
      }}
      clearOnBlur={false}
      loading={list.isLoading}
      style={{ flex: 1 }}
      renderInput={(params) => (
        <PmiTextField
          {...params}
          label={required ? `${label} *` : label}
          variant="outlined"
          error={error}
        />
      )}
    />
  );
};

StationIdAnprListComponent.displayName = 'Station id Anpr list component';

const StationIdAnprList = memo(StationIdAnprListComponent);

export default StationIdAnprList as typeof StationIdAnprListComponent;
